import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  Loader2,
  Save,
  Plus,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'PRE_ORDER' | 'DISCONTINUED';

interface Specification {
  key: string;
  value: string;
}

interface ProductFormData {
  modelId: string;
  code: string;
  sku?: string;
  name: string;
  slug: string;
  shortDescription?: string;
  description?: string;
  price?: number;
  currency: string;
  priceOnRequest: boolean;
  markupType?: string | null;
  markupValue?: number | null;
  stockStatus: StockStatus;
  minOrderQty: number;
  isActive: boolean;
  isFeatured: boolean;
  sortOrder: number;
}

export function NewTradingProductPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultModelId = searchParams.get('modelId') || undefined;
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [specifications, setSpecifications] = useState<Specification[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProductFormData>({
    defaultValues: {
      modelId: defaultModelId || '',
      code: '',
      sku: '',
      name: '',
      slug: '',
      shortDescription: '',
      description: '',
      price: undefined,
      currency: 'EUR',
      priceOnRequest: true,
      markupType: null,
      markupValue: null,
      stockStatus: 'IN_STOCK',
      minOrderQty: 1,
      isActive: true,
      isFeatured: false,
      sortOrder: 0,
    },
  });

  const utils = trpc.useUtils();

  const { data: brandsData } = trpc.tradingBrand.list.useQuery({});
  const brands = brandsData?.brands ?? [];

  const { data: modelsData } = trpc.tradingModel.list.useQuery(
    { brandId: selectedBrandId },
    { enabled: !!selectedBrandId }
  );
  const models = modelsData?.models ?? [];

  const watchModelId = watch('modelId');
  const watchPriceOnRequest = watch('priceOnRequest');
  const watchMarkupType = watch('markupType');
  const watchName = watch('name');

  const { data: nextCodeData } = trpc.tradingProduct.getNextCode.useQuery(
    watchModelId,
    { enabled: !!watchModelId }
  );

  const createMutation = trpc.tradingProduct.create.useMutation({
    onSuccess: (data) => {
      utils.tradingProduct.list.invalidate();
      utils.tradingModel.list.invalidate();
      showSuccessToast(t('common.created'));
      navigate(`/trading/products/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  // Auto-set code and sortOrder when model changes
  useEffect(() => {
    if (nextCodeData) {
      setValue('code', nextCodeData.code);
      setValue('sortOrder', nextCodeData.sortOrder);
    }
  }, [nextCodeData, setValue]);

  // Auto-generate slug from name
  useEffect(() => {
    if (watchName) {
      const slug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('slug', slug);
    }
  }, [watchName, setValue]);

  const addSpecification = () => {
    setSpecifications([...specifications, { key: '', value: '' }]);
  };

  const removeSpecification = (index: number) => {
    setSpecifications(specifications.filter((_, i) => i !== index));
  };

  const updateSpecification = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...specifications];
    const spec = updated[index];
    if (spec) {
      spec[field] = value;
      setSpecifications(updated);
    }
  };

  const onSubmit = (data: ProductFormData) => {
    const specsObject = specifications.reduce((acc, spec) => {
      if (spec.key.trim()) {
        acc[spec.key.trim()] = spec.value;
      }
      return acc;
    }, {} as Record<string, string>);

    const payload = {
      modelId: data.modelId,
      code: data.code,
      name: data.name,
      slug: data.slug,
      currency: data.currency,
      priceOnRequest: data.priceOnRequest,
      markupType: (data.markupType as 'FLAT' | 'PERCENTAGE') || undefined,
      markupValue: data.markupType ? data.markupValue : undefined,
      stockStatus: data.stockStatus,
      minOrderQty: data.minOrderQty,
      isActive: data.isActive,
      isFeatured: data.isFeatured,
      sortOrder: data.sortOrder,
      sku: data.sku || undefined,
      shortDescription: data.shortDescription || undefined,
      description: data.description || undefined,
      price: data.priceOnRequest ? undefined : data.price,
      specifications: Object.keys(specsObject).length > 0 ? specsObject : undefined,
    };

    createMutation.mutate(payload);
  };

  const isSubmitting = createMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="h-8" onClick={() => navigate('/trading/products')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('products.backToList')}
          </Button>
          <div className="h-4 w-px bg-border" />
          <h1 className="text-lg font-semibold">{t('products.addProduct')}</h1>
        </div>
        <Button
          className="h-8"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || !watchModelId}
        >
          {isSubmitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {tCommon('create')}
        </Button>
      </div>

      <form className="grid gap-4 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basic Info */}
          <div className="border border-border p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('products.basicInfo')}</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.brand')}</Label>
                <select
                  value={selectedBrandId}
                  onChange={(e) => {
                    setSelectedBrandId(e.target.value);
                    setValue('modelId', '');
                  }}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                >
                  <option value="">{t('products.selectBrand')}</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.code} - {brand.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.model')} *</Label>
                <select
                  {...register('modelId', { required: t('common.required') })}
                  disabled={!selectedBrandId}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                >
                  <option value="">{t('products.selectModel')}</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.code} - {model.name}
                    </option>
                  ))}
                </select>
                {errors.modelId && (
                  <p className="text-xs text-red-500">{errors.modelId.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.code')} *</Label>
                <input
                  {...register('code', { required: t('common.required') })}
                  placeholder="001"
                  maxLength={3}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                />
                {errors.code && (
                  <p className="text-xs text-red-500">{errors.code.message}</p>
                )}
                <p className="text-[10px] text-muted-foreground">{t('products.codeHelp')}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.sku')}</Label>
                <input
                  {...register('sku')}
                  placeholder={t('products.skuPlaceholder')}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.sortOrder')}</Label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.name')} *</Label>
                <input
                  {...register('name', { required: t('common.required') })}
                  placeholder={t('products.namePlaceholder')}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.slug')} *</Label>
                <input
                  {...register('slug', { required: t('common.required') })}
                  placeholder="product-name"
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                />
                {errors.slug && (
                  <p className="text-xs text-red-500">{errors.slug.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t('products.shortDescription')}</Label>
              <input
                {...register('shortDescription')}
                className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t('products.description')}</Label>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full border border-border bg-background px-2 py-1.5 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 resize-none"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="border border-border p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('products.pricing')}</h2>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register('priceOnRequest')}
                  className="h-4 w-4 border-border"
                />
                {t('products.priceOnRequest')}
              </label>
            </div>

            {!watchPriceOnRequest && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.price')}</Label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('price', { valueAsNumber: true })}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.currency')}</Label>
                  <select
                    {...register('currency')}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
              </div>
            )}

            {/* Markup */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.markupType')}</Label>
                <select
                  {...register('markupType')}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                >
                  <option value="">{t('products.noMarkup')}</option>
                  <option value="FLAT">{t('products.flatMarkup')}</option>
                  <option value="PERCENTAGE">{t('products.percentageMarkup')}</option>
                </select>
              </div>
              {watchMarkupType && (
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    {watchMarkupType === 'PERCENTAGE' ? t('products.markupPercentage') : t('products.markupAmount')}
                  </Label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('markupValue', { valueAsNumber: true })}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.stockStatus')}</Label>
                <select
                  {...register('stockStatus')}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                >
                  <option value="IN_STOCK">{t('products.inStock')}</option>
                  <option value="LOW_STOCK">{t('products.lowStock')}</option>
                  <option value="OUT_OF_STOCK">{t('products.outOfStock')}</option>
                  <option value="PRE_ORDER">{t('products.preOrder')}</option>
                  <option value="DISCONTINUED">{t('products.discontinued')}</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.minOrderQty')}</Label>
                <input
                  type="number"
                  min={1}
                  {...register('minOrderQty', { valueAsNumber: true })}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                />
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="border border-border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">{t('products.specifications')}</h2>
              <Button
                type="button"
                variant="outline"
                className="h-7 text-xs"
                onClick={addSpecification}
              >
                <Plus className="mr-1 h-3 w-3" />
                {t('products.addSpec')}
              </Button>
            </div>

            {specifications.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('products.noSpecs')}</p>
            ) : (
              <div className="space-y-2">
                {specifications.map((spec, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      placeholder={t('products.specKey')}
                      value={spec.key}
                      onChange={(e) => updateSpecification(index, 'key', e.target.value)}
                      className="h-8 flex-1 border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                    />
                    <input
                      placeholder={t('products.specValue')}
                      value={spec.value}
                      onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                      className="h-8 flex-1 border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500 hover:text-red-600"
                      onClick={() => removeSpecification(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status */}
          <div className="border border-border p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('products.status')}</h2>

            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 border-border"
                />
                {t('products.active')}
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register('isFeatured')}
                  className="h-4 w-4 border-border"
                />
                {t('products.featured')}
              </label>
            </div>
          </div>

          {/* Help */}
          <div className="border border-border p-4 space-y-2">
            <h2 className="text-sm font-medium">{t('products.help')}</h2>
            <p className="text-xs text-muted-foreground">
              {t('products.helpText')}
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
